import { supabase } from "./supabase";

// O IO do ForoFácil (000169/000170) — e as poucas regras puras dele.
//
// Este módulo transporta e conta. Quem decide se o aluno pode ler o catálogo é a
// RLS (`conteudo_liberado('foro-facil') or eh_admin()`); quem decide se ele pode
// GRAVAR é a RPC (`foro_definir_status`, `foro_marcar_documento`), que nunca
// recebe id de aluno. Aqui não há regra de acesso.
//
// ---------------------------------------------------------------------------
// O progresso é DADO do aluno, e o mapa mostra o FATO
// ---------------------------------------------------------------------------
// Na origem (o app da Lovable) o status de cada tribunal e os tiques dos
// documentos moravam em `localStorage['tj-facil-progress']` — troca de
// navegador, zero. E o mapa recebia o progresso e o IGNORAVA: pintava por
// região (TJ) ou por TRF, que é decoração. Aqui as duas tabelas de progresso
// são do dono, por RLS, e `corDoStatus` é o que pinta o mapa.
//
// O tique aponta para o `id` do documento, não para a POSIÇÃO na lista, que era
// o desenho da origem (`docs: boolean[]`): reordenar um documento lá trocava o
// tique de todo mundo em silêncio.

export type TipoDeTribunal = "tj" | "trf";
export type TipoDeDocumento = "pf" | "pj";
/** `nao_iniciado` não é gravado: é a AUSÊNCIA de linha em `foro_cadastros`. */
export type StatusDoCadastro = "nao_iniciado" | "em_andamento" | "concluido";

export type Tribunal = {
  sigla: string;
  tipo: TipoDeTribunal;
  nome: string;
  nome_completo: string;
  uf: string | null;
  regiao: string | null;
  /** O TRF da UF — só nos TJs. */
  trf: string | null;
  /** Só nos TRFs. */
  sede: string | null;
  sistema: string;
  site: string;
  link_cadastro: string;
  edital: string | null;
  tutorial: string | null;
  alerta: string | null;
  contato: string | null;
  nota_de_origem: string | null;
  conferido_em: string | null;
};

export type Documento = {
  id: string;
  tribunal: string;
  tipo: TipoDeDocumento;
  texto: string;
  ordem: number;
  ativo: boolean;
};

export type Catalogo = { tribunais: Tribunal[]; documentos: Documento[] };

export type Progresso = {
  /** sigla → status; quem não está aqui é `nao_iniciado`. */
  status: Map<string, StatusDoCadastro>;
  /** ids dos documentos marcados. */
  marcados: Set<string>;
};

export const BASE = "/ferramenta/foro-facil";

export const STATUS: { valor: StatusDoCadastro; rotulo: string }[] = [
  { valor: "nao_iniciado", rotulo: "Não iniciado" },
  { valor: "em_andamento", rotulo: "Em andamento" },
  { valor: "concluido", rotulo: "Concluído" },
];

export function rotuloDoStatus(s: StatusDoCadastro): string {
  return STATUS.find((x) => x.valor === s)?.rotulo ?? s;
}

/**
 * A cor de cada status no mapa — TOKEN da escala do coroplético, nunca cor de
 * marca. Os três degraus saem da escala já medida do Mapa de Concursos
 * (`--cf-mapa-*`): o zero RECUA (não iniciado é a maioria, e estado apagado é
 * informação), o meio é o azul claro e o topo é o laranja — dois MATIZES
 * diferentes, porque uma rampa só de luminância não separa "em andamento" de
 * "concluído" no olho. O mapa mora dentro de `painel-navy`, escuro nos dois
 * temas, e é o território que define estas variáveis.
 */
export function corDoStatus(s: StatusDoCadastro): string {
  if (s === "concluido") return "var(--cf-mapa-4)";
  if (s === "em_andamento") return "var(--cf-mapa-2)";
  return "var(--cf-mapa-0)";
}

export function statusDe(progresso: Progresso | null, sigla: string): StatusDoCadastro {
  return progresso?.status.get(sigla) ?? "nao_iniciado";
}

/**
 * A cor de cada REGIÃO no modo TRF — e por que ela não é a escala de status.
 *
 * ---------------------------------------------------------------------------
 * O defeito que isto conserta (03/09/2026)
 * ---------------------------------------------------------------------------
 * A primeira versão pintava o modo TRF pelo STATUS, igual ao modo TJ, e a
 * legenda ao lado prometia outra coisa: *"os estados estão pintados pelo TRF a
 * que pertencem"*. Como ninguém tem cadastro no primeiro dia, os 27 estados
 * saíam todos no degrau ZERO — que RECUA de propósito — e o mapa dos federais
 * era uma mancha só. Victor viu na tela: *"os estados do TRF não ficaram
 * pintados"*. A frase estava certa e o mapa é que não cumpria.
 *
 * O erro de fundo foi tratar região como QUANTIDADE. Rampa de luminância serve
 * para quantidade (é o coroplético do Mapa de Concursos); região é CATEGORIA, e
 * categoria se separa por MATIZ — é o que o Legaliza faz com os 4 integradores.
 * Medido antes de escolher: com uma rampa de ardósia de seis degraus, a melhor
 * atribuição possível ainda deixava o Rio contra São Paulo em **1,29 : 1**, ou
 * seja a mesma mancha com mais passos.
 *
 * Estes seis matizes foram medidos em ΔE (distância perceptual em Lab; o
 * contraste WCAG é só luminância e não serviria aqui):
 *
 *   · a MENOR separação entre duas regiões que fazem fronteira é **ΔE 20,1**
 *     (TRF1 × TRF3, no encontro de Mato Grosso e Goiás com Mato Grosso do Sul);
 *   · contra o navy, o mais discreto é o TRF1 com ΔE 22,1 (2,06 : 1);
 *   · contra as duas cores de STATUS, o mais próximo é ΔE 41,6 — nenhum tom de
 *     região se confunde com "em andamento" nem com "concluído".
 *
 * É essa última medida que deixa as duas informações conviverem no mesmo mapa:
 * a região é o FUNDO, e o status pinta POR CIMA quando existe. Ou seja o mapa
 * continua sendo o progresso — o que mudou é que ele não mente mais no repouso.
 */
const COR_DA_REGIAO: Record<string, string> = {
  TRF1: "#3d5a80", // azul-ardósia
  TRF2: "#4f7d6a", // verde-pinho
  TRF3: "#6b5b95", // violeta acinzentado
  TRF4: "#2f6f7d", // petróleo
  TRF5: "#8a5f6d", // vinho acinzentado
  TRF6: "#6d6a4a", // oliva
};

export function corDaRegiao(siglaTrf: string): string {
  // O fallback é o degrau zero: um TRF que o catálogo não conheça não pode
  // pintar de branco no meio do navy.
  return COR_DA_REGIAO[siglaTrf] ?? "var(--cf-mapa-0)";
}

/**
 * A cor de um estado no modo TRF: o status quando ele existe, a região quando
 * não. Um lugar só, porque o mapa e a legenda têm de concordar.
 */
export function corNoModoTrf(progresso: Progresso | null, siglaTrf: string): string {
  const s = statusDe(progresso, siglaTrf);
  return s === "nao_iniciado" ? corDaRegiao(siglaTrf) : corDoStatus(s);
}

/**
 * O nome do tribunal SEM o prefixo que se repete em toda linha da lista.
 *
 * "Tribunal de Justiça" aparece 27 vezes numa lista de 27, e "Tribunal Regional
 * Federal" 6 vezes numa de 6 — vinte e um caracteres antes de começar a parte
 * que distingue uma linha da outra. Com a lista dentro de uma COLUNA (o redesenho
 * de 03/09), isso truncava metade dos nomes: "Tribunal de Justiça de Mato Gros…"
 * duas vezes seguidas, para dois estados diferentes. É a cicatriz do IR Pro —
 * quando a tela parece cheia e não diz nada a mais, conte quantas vezes a mesma
 * informação aparece —, e quem diz o tipo é o título da seção, uma vez.
 *
 * O prefixo sai por correspondência EXATA; o que não casar volta inteiro. A lista
 * nunca inventa nome, e o nome completo continua no `title` da linha.
 */
export function nomeNaLista(t: Tribunal): string {
  const m = t.nome_completo.match(/^Tribunal (?:de Justiça|Regional Federal) d[aoe]s? (.+)$/);
  return m ? m[1] : t.nome_completo;
}

/** Os documentos LIGADOS de um tribunal, de um tipo, na ordem da equipe. */
export function documentosDo(catalogo: Catalogo, sigla: string, tipo: TipoDeDocumento): Documento[] {
  return catalogo.documentos
    .filter((d) => d.tribunal === sigla && d.tipo === tipo && d.ativo)
    .sort((a, b) => a.ordem - b.ordem || a.texto.localeCompare(b.texto, "pt-BR"));
}

/** Os TJs sob a jurisdição de um TRF — DERIVADO da coluna `trf`, num lugar só. */
export function jurisdicaoDoTrf(catalogo: Catalogo, siglaTrf: string): Tribunal[] {
  return catalogo.tribunais
    .filter((t) => t.tipo === "tj" && t.trf === siglaTrf)
    .sort((a, b) => a.sigla.localeCompare(b.sigla));
}

export function tribunaisDoTipo(catalogo: Catalogo, tipo: TipoDeTribunal): Tribunal[] {
  return catalogo.tribunais
    .filter((t) => t.tipo === tipo)
    .sort((a, b) => a.nome_completo.localeCompare(b.nome_completo, "pt-BR"));
}

/**
 * Os fatos da cabine — contados, nunca inventados. Zero grande é ruído: quem
 * chama esconde o tile quando o número é zero.
 */
export function resumo(catalogo: Catalogo, progresso: Progresso | null) {
  let concluidos = 0;
  let emAndamento = 0;
  for (const t of catalogo.tribunais) {
    const s = statusDe(progresso, t.sigla);
    if (s === "concluido") concluidos++;
    else if (s === "em_andamento") emAndamento++;
  }
  const ativos = new Set(catalogo.documentos.filter((d) => d.ativo).map((d) => d.id));
  let marcados = 0;
  for (const id of progresso?.marcados ?? []) if (ativos.has(id)) marcados++;
  return { concluidos, emAndamento, marcados, tribunais: catalogo.tribunais.length };
}

/** Quantos dos documentos LIGADOS de um tribunal o aluno já marcou. */
export function marcadosDo(
  catalogo: Catalogo,
  progresso: Progresso | null,
  sigla: string,
): { marcados: number; total: number } {
  const docs = catalogo.documentos.filter((d) => d.tribunal === sigla && d.ativo);
  const marcados = docs.filter((d) => progresso?.marcados.has(d.id)).length;
  return { marcados, total: docs.length };
}

/**
 * As Dicas de Aceite — constante, e não tabela.
 *
 * O corte da 000108 pergunta QUEM revê o valor: estas frases são redação da
 * casa e mudam com revisão da equipe pedagógica, que é deploy de todo modo.
 * Links de tribunal mudam por ato de terceiro e por isso estão no banco; um
 * parágrafo de orientação, não. Vieram da origem (`TipsView.tsx`) como estavam.
 */
export const DICAS: { titulo: string; tom: "sucesso" | "aviso" | "erro"; itens: string[] }[] = [
  {
    titulo: "Como aumentar as chances de aceite",
    tom: "sucesso",
    itens: [
      "Mantenha todos os documentos atualizados e digitalizados em alta resolução.",
      "Invista em cursos de capacitação específicos para perícia judicial.",
      "Construa um currículo focado: destaque experiências relevantes para a área pericial.",
      "Acompanhe os editais de cada tribunal regularmente.",
      "Obtenha certificado digital A3 — muitos tribunais exigem.",
    ],
  },
  {
    titulo: "Erros comuns no cadastro",
    tom: "erro",
    itens: [
      "Enviar documentos vencidos ou com informações desatualizadas.",
      "Não preencher todos os campos obrigatórios do formulário.",
      "Usar foto inadequada (informal ou em baixa resolução).",
      "Esquecer de assinar digitalmente quando exigido.",
      "Não acompanhar o status após o envio do cadastro.",
    ],
  },
  {
    titulo: "O que os tribunais valorizam",
    tom: "aviso",
    itens: [
      "Experiência comprovada na área de atuação.",
      "Participação em cursos e eventos de atualização profissional.",
      "Disponibilidade para atuar em comarcas diversas.",
      "Currículo bem estruturado e objetivo.",
      "Pontualidade e qualidade na entrega de laudos anteriores.",
    ],
  },
];

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

const COLUNAS_DO_TRIBUNAL =
  "sigla,tipo,nome,nome_completo,uf,regiao,trf,sede,sistema,site,link_cadastro,edital,tutorial,alerta,contato,nota_de_origem,conferido_em";

/**
 * O catálogo inteiro numa ida só: 33 tribunais e ~400 documentos são linhas
 * pequenas, e o mapa é feito para ser percorrido — buscar por tribunal exigiria
 * uma ida ao servidor a cada clique. Mesma decisão de `lib/legaliza.ts`.
 */
export async function lerCatalogo(): Promise<Catalogo | null> {
  const [tribunais, documentos] = await Promise.all([
    supabase.from("foro_tribunais").select(COLUNAS_DO_TRIBUNAL).order("nome_completo"),
    supabase
      .from("foro_documentos")
      .select("id,tribunal,tipo,texto,ordem,ativo")
      .eq("ativo", true)
      .order("ordem"),
  ]);
  if (tribunais.error || documentos.error) return null;
  return {
    tribunais: (tribunais.data ?? []) as Tribunal[],
    documentos: (documentos.data ?? []) as Documento[],
  };
}

/**
 * O progresso do aluno da sessão. Um `select` cru é seguro AQUI porque a policy
 * das duas tabelas é "só o dono" — sem `or eh_suporte()`. É a condição que a
 * cicatriz da 000136 põe para ler sem função: onde a policy tem a saída da
 * equipe, a leitura do aluno sai por função com filtro explícito.
 */
export async function lerMeuProgresso(): Promise<Progresso | null> {
  const [cadastros, marcados] = await Promise.all([
    supabase.from("foro_cadastros").select("tribunal,status"),
    supabase.from("foro_documentos_marcados").select("documento_id"),
  ]);
  if (cadastros.error || marcados.error) return null;
  return {
    status: new Map(
      (cadastros.data ?? []).map((c) => [c.tribunal as string, c.status as StatusDoCadastro]),
    ),
    marcados: new Set((marcados.data ?? []).map((m) => m.documento_id as string)),
  };
}

// ---------------------------------------------------------------------------
// Escrita do aluno — por RPC, nunca por insert
// ---------------------------------------------------------------------------

/** Devolve a mensagem do banco em caso de recusa; nulo quando gravou. */
export async function definirStatus(sigla: string, status: StatusDoCadastro): Promise<string | null> {
  const { error } = await supabase.rpc("foro_definir_status", {
    p_tribunal: sigla,
    p_status: status,
  });
  return error ? error.message : null;
}

export async function marcarDocumento(id: string, marcado: boolean): Promise<string | null> {
  const { error } = await supabase.rpc("foro_marcar_documento", {
    p_documento: id,
    p_marcado: marcado,
  });
  return error ? error.message : null;
}

// ---------------------------------------------------------------------------
// Administração
// ---------------------------------------------------------------------------

export type TribunalParaAdmin = Tribunal & {
  conferido_por: string | null;
  /** Quem conferiu, pelo nome — embutido pela FK `conferido_por → profiles`. */
  conferidor: { nome: string | null } | null;
};

/** Os 33 com o NOME de quem conferiu — a tela de admin tem de dizer QUEM. */
export async function lerTribunaisParaAdmin(): Promise<TribunalParaAdmin[] | null> {
  const { data, error } = await supabase
    .from("foro_tribunais")
    .select(`${COLUNAS_DO_TRIBUNAL},conferido_por,conferidor:profiles!conferido_por(nome)`)
    .order("tipo")
    .order("nome_completo");
  if (error) return null;
  return (data ?? []) as unknown as TribunalParaAdmin[];
}

/**
 * Os documentos INATIVOS também: uma tela de administração que só enxerga o
 * que está ligado não consegue religar nada (a lição da 000090 pelo avesso).
 */
export async function lerDocumentosParaAdmin(): Promise<Documento[] | null> {
  const { data, error } = await supabase
    .from("foro_documentos")
    .select("id,tribunal,tipo,texto,ordem,ativo")
    .order("tribunal")
    .order("tipo")
    .order("ordem");
  if (error) return null;
  return (data ?? []) as Documento[];
}

/**
 * Salva os campos comuns de um tribunal. Links NÃO passam por aqui: mexer num
 * link tem consequência (4 mil alunos clicam nele) e carimbo de conferência;
 * trocar o texto do alerta não tem.
 */
export async function salvarTribunal(
  sigla: string,
  campos: {
    nome: string;
    nome_completo: string;
    sistema: string;
    alerta: string | null;
    contato: string | null;
    nota_de_origem: string | null;
  },
): Promise<string | null> {
  const { error } = await supabase.from("foro_tribunais").update(campos).eq("sigla", sigla);
  return error ? error.message : null;
}

/**
 * Grava os quatro links e carimba a conferência — os dois no mesmo ato, pelo
 * servidor (RPC). Para edital e tutorial, string VAZIA apaga: "este tribunal
 * não publica edital" é uma conclusão legítima da conferência.
 */
export async function confirmarTribunal(
  sigla: string,
  links: { site: string; link_cadastro: string; edital: string; tutorial: string },
): Promise<string | null> {
  const { error } = await supabase.rpc("foro_confirmar_tribunal", {
    p_sigla: sigla,
    p_site: links.site,
    p_link_cadastro: links.link_cadastro,
    p_edital: links.edital,
    p_tutorial: links.tutorial,
  });
  return error ? error.message : null;
}

/** Corrige o texto ou a ordem de um documento, ou o liga/desliga. Nunca apaga. */
export async function salvarDocumento(
  id: string,
  campos: Partial<Pick<Documento, "texto" | "ordem" | "ativo">>,
): Promise<string | null> {
  const { error } = await supabase.from("foro_documentos").update(campos).eq("id", id);
  return error ? error.message : null;
}

export async function adicionarDocumento(
  tribunal: string,
  tipo: TipoDeDocumento,
  texto: string,
  ordem: number,
): Promise<string | null> {
  const { error } = await supabase
    .from("foro_documentos")
    .insert({ tribunal, tipo, texto: texto.trim(), ordem });
  return error ? error.message : null;
}
